import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AgentReferences, AgentSpec, CreateAgentRequest, RetireAgentResult,
} from '../../../types/backend';
import { useAgentSpecService } from '../services/agentSpecService';
import { runnableAgentKeys, useRunnableAgents } from '../../../shared/hooks/useRunnableAgents';
import { agentSpecKeys } from './useAgentSpecForm';

export const agentLifecycleKeys = {
  references: (agentId: string) => ['prompt-studio', 'agent-references', agentId] as const,
};

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err));

/**
 * Retiring one agent (PS-UC-18): what still names it, read before the admin confirms, and the
 * retire itself. The retired row replaces the active one in the cached list.
 */
export function useRetireAgent(agentId: string, open: boolean) {
  const service = useAgentSpecService();
  const queryClient = useQueryClient();

  const referencesQuery = useQuery<AgentReferences>({
    queryKey: agentLifecycleKeys.references(agentId),
    queryFn: () => service.listRulesNamingAgent(agentId),
    enabled: open,
    // Read fresh on every open: a rule may have named the agent since the last answer.
    staleTime: 0,
  });

  const retireMutation = useMutation<RetireAgentResult, Error>({
    mutationFn: () => service.retireAgent(agentId),
    onSuccess: (result) => {
      queryClient.setQueryData<AgentSpec[]>(agentSpecKeys.list(), (prev) =>
        prev?.map((s) => (s.agent_id === result.agent.agent_id ? result.agent : s)),
      );
      queryClient.invalidateQueries({ queryKey: runnableAgentKeys.all() });
    },
  });

  return {
    references: referencesQuery.data ?? null,
    referencesError: referencesQuery.isError ? message(referencesQuery.error) : null,
    retire: () => retireMutation.mutate(),
    retiring: retireMutation.isPending,
    result: retireMutation.data ?? null,
    error: retireMutation.isError ? message(retireMutation.error) : null,
    reset: retireMutation.reset,
  };
}

/**
 * Making a new agent (PS-UC-15): the specialists it may name as callees, and the create. The new
 * row joins the cached list.
 */
export function useCreateAgent(open: boolean) {
  const service = useAgentSpecService();
  const queryClient = useQueryClient();

  const specialists = useRunnableAgents('sub_agent', open);

  const createMutation = useMutation<AgentSpec, Error, CreateAgentRequest>({
    mutationFn: (request) => service.createAgent(request),
    onSuccess: (created) => {
      queryClient.setQueryData<AgentSpec[]>(agentSpecKeys.list(), (prev) => (prev ? [...prev, created] : prev));
      queryClient.invalidateQueries({ queryKey: runnableAgentKeys.all() });
    },
  });

  return {
    specialists: specialists.agents,
    specialistsError: specialists.error,
    create: createMutation.mutateAsync,
    creating: createMutation.isPending,
    error: createMutation.isError ? message(createMutation.error) : null,
    reset: createMutation.reset,
  };
}
